import { Component, OnInit } from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import { MainService } from "../../../services/main.service";
import { Api } from "../../../common/api";
import { FormBuilder, FormGroup, FormArray, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { IProperty } from "../../../common/property";
import { Constants } from "../../../common/constants";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: "app-edit-user",
  templateUrl: "./edit-user.component.html",
  styleUrls: ["./edit-user.component.scss"],
})
export class EditUserComponent implements OnInit {
  editUserForm: FormGroup;
  submitted = false;
  customizeData: any;
  public parameter: IProperty = {};
  userId: any;
  profilePicFile: File | null = null;
  imagesFiles: File[] = [];
  officialFiles: File[] = [];
  interviewFile: File | null = null;

  constructor(
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private router: Router,
    private mainService: MainService,
    private api: Api,
    private formBuilder: FormBuilder,
    public constants: Constants,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.addFormValidator();
    this.route.params.subscribe((params) => {
      this.userId = params["userId"];
    });

    // Add initial FD and Saving fields if needed
    this.addFD();
    this.addSaving();

    const data = {
      userId: this.userId,
    };

    this.mainService
      .getDataAptWithParams(this.api.paths.getUserDetails, data)
      .subscribe((success) => {
        this.customizeData = success["data"];
        this.editUserForm.patchValue(this.customizeData);
      });
  }

  // Getter for FDs FormArray
  get fds(): FormArray {
    return this.editUserForm.get("fds") as FormArray;
  }

  // Getter for Savings FormArray
  get savings(): FormArray {
    return this.editUserForm.get("savings") as FormArray;
  }

  // Add a new FD field
  addFD(): void {
    const fdGroup = this.formBuilder.group({
      fdReference: ["", Validators.required], // FD Reference Number
      bankName: ["", Validators.required], // Bank Name
      fdAmount: ["", Validators.required], // Amount
    });
    this.fds.push(fdGroup);
  }

  // Remove an FD field
  removeFD(index: number): void {
    this.fds.removeAt(index);
  }

  // Add a new Saving field
  addSaving(): void {
    const savingGroup = this.formBuilder.group({
      bankName: ["", Validators.required], // Bank Name
      accountNumber: ["", Validators.required], // Account Number
      savingAmount: ["", Validators.required], // Amount
    });
    this.savings.push(savingGroup);
  }

  // Remove a Saving field
  removeSaving(index: number): void {
    this.savings.removeAt(index);
  }

  addFormValidator() {
    this.editUserForm = this.formBuilder.group({
      fullName: [""],
      otherName: [""],
      gender: [""],
      dob: [""],
      fileSubmitDate: [""],
      profilePic: [""], // We'll store the file name here if needed
      placeOfBirth: [""],
      citizenship: [""],
      currentCountry: [""],
      countryApply: [""],
      maritalStatus: [""],
      spouseName: [""],
      nativeLanguage: [""],
      passportNumber: [""],
      passportCountry: [""],
      passportIssue: [""],
      passportExpiry: [""],
      nationalIdentity: [""],
      usPrCard: [""],
      visitFrom: [""],
      visitTo: [""],
      email: [""],
      phone: [""],
      altPhone: [""],
      address: [""],
      fax: [""],
      purposeVisit: [""],
      schoolName: [""],
      schoolAddress: [""],
      schoolSessionYear: [""],
      finance: [""],
      fundsAvailable: [""],
      education: [""],
      tuberculosis: [""],
      medicalDisorder: [""],
      arrested: [""],
      political: [""],
      fileStatus: [""],
      fatherName: [""],
      fatherAge: [""],
      fatherEducation: [""],
      fatherJob: [""],
      fatherIncome: [""],
      fatherPassport: [""],
      motherName: [""],
      motherAge: [""],
      motherEducation: [""],
      motherJob: [""],
      motherIncome: [""],
      motherPassport: [""],
      siblings: [""],
      applicationFee: [""],
      lawyerFee: [""],
      lawyerInteractionFee: [""],
      tutionFee: [""],
      validity: [""],
      remarks: [""],
      fd1: [""],
      fd2: [""],
      saving1: [""],
      saving2: [""],
      sibling1: [""],
      siblingAge1: [""],
      siblingGender1: [""],
      sibling2: [""],
      siblingGender2: [""],
      siblingAge2: [""],
      sibling3: [""],
      siblingGender3: [""],
      siblingAge3: [""],
      sibling4: [""],
      siblingGender4: [""],
      siblingAge4: [""],
      inviteeName: [""],
      inviteeFatherName: [""],
      inviteePassportNumber: [""],
      inviteeRelation: [""],

      images: [[]],
      fds: this.formBuilder.array([]), // FormArray for FDs
      savings: this.formBuilder.array([]), // FormArray for Savings // We'll store the file names or file data here if needed
    });
  }

  onFileChange(event: any, field: string) {
    if (field === "profilePic") {
      this.profilePicFile = event.target.files[0] || null;
    } else if (field === "images") {
      this.imagesFiles = Array.from(event.target.files);
    } else if (field == "official") {
      this.officialFiles = Array.from(event.target.files);
    } else if (field === "interview") {
      this.interviewFile = event.target.files[0] || null;
    }
  }

  editUser() {
    console.log("this.", this.editUserForm.value);
    this.submitted = true;
    if (this.editUserForm.invalid) {
      return;
    }

    const formData = new FormData();
    formData.append("userId", this.userId);

    // Add the profile pic if available
    if (this.profilePicFile) {
      formData.append("profilePic", this.profilePicFile);
    }

    // Add the interview file if available
    if (this.interviewFile) {
      formData.append("interview", this.interviewFile);
    }

    // Add the multiple images if available
    if (this.imagesFiles.length > 0) {
      this.imagesFiles.forEach((file, index) => {
        formData.append("images", file);
      });
    }

    if (this.officialFiles.length > 0) {
      this.officialFiles.forEach((file, index) => {
        formData.append("official", file);
      });
    }

    // Correctly append FDs and Savings
    // Append dynamic FDs to form data
    this.fds.controls.forEach((control, index) => {
      formData.append(
        `fds[${index}][fdReference]`,
        control.get("fdReference")?.value || ""
      );
      formData.append(
        `fds[${index}][bankName]`,
        control.get("bankName")?.value || ""
      );
      formData.append(
        `fds[${index}][fdAmount]`,
        control.get("fdAmount")?.value || ""
      );
    });

    // Append dynamic Savings to form data
    this.savings.controls.forEach((control, index) => {
      formData.append(
        `savings[${index}][bankName]`,
        control.get("bankName")?.value || ""
      );
      formData.append(
        `savings[${index}][accountNumber]`,
        control.get("accountNumber")?.value || ""
      );
      formData.append(
        `savings[${index}][savingAmount]`,
        control.get("savingAmount")?.value || ""
      );
    });

    // Append other form data
    for (const key in this.editUserForm.value) {
      if (
        this.editUserForm.value.hasOwnProperty(key) &&
        key !== "profilePic" &&
        key !== "images" &&
        key !== "fds" && // Exclude fds
        key !== "savings" // Exclude savings
      ) {
        formData.append(key, this.editUserForm.value[key]);
      }
    }

    this.mainService
      .postDataApi(this.api.paths.editUser, formData)
      .subscribe((success) => {
        this.toastrService.success("User updated successfully");
        this.router.navigate(["users"]);
      });
  }

  get lf() {
    return this.editUserForm.controls;
  }
}
